import { QuestionnaireFormData } from 'common';
import { z } from 'zod';
import { QuestionnaireSimSchema, buildQuestionnairesArraySchema } from './QuestionnaireSim/questionnaireSimSchema';
import { TaxDetailsSchema } from './TaxDetails';
import { RemittanceSchema } from './Remittance';
import { CertificateOfInsuranceSchema } from './CertificateOfInsurance';
import { SupplierPendingSimRequestFlags } from '../useSupplierPendingSimRequests';

/**
 * Combined schema for all post-signup steps, mirroring the
 * MultiStepForm pattern: one top-level object with one key per step.
 *
 * The orchestrator always renders the form against this full schema
 * (react-hook-form + zodResolver) even when only a subset of steps is
 * shown, so every key needs a valid sub-schema. Unused keys are simply
 * left untouched / default-empty.
 *
 * Drives the static `PostSignupFormData` type used by step components.
 * For runtime validation, use {@link buildPostSignupFormSchema} so the
 * questionnaire step can enforce required-field rules per buyer-defined
 * questionnaire.
 */
export const PostSignupFormSchema = z.object({
    taxDetails: TaxDetailsSchema,
    remittance: RemittanceSchema,
    certificateOfInsurance: CertificateOfInsuranceSchema,
    questionnaires: QuestionnaireSimSchema.default([]),
});

export type PostSignupFormData = z.infer<typeof PostSignupFormSchema>;

/**
 * Runtime schema parallel to {@link PostSignupFormSchema}. Identical
 * inferred type, but:
 * - sections whose step is not rendered (flag off) skip validation —
 *   their untouched defaults would otherwise fail final submit;
 * - `questionnaires` is refined from each item's required flag.
 *
 * Pass this to `<MultiStepForm schema={...}>`. Step components stay
 * typed against `PostSignupFormSchema`, since the form data shape is
 * identical — only validation rules differ.
 */
export function buildPostSignupFormSchema(
    pendingQuestionnaires: QuestionnaireFormData[],
    flags: SupplierPendingSimRequestFlags
) {
    return z.object({
        taxDetails: flags.taxDetails ? TaxDetailsSchema : z.any(),
        remittance: flags.remittance ? RemittanceSchema : z.any(),
        certificateOfInsurance: flags.certificateOfInsurance ? CertificateOfInsuranceSchema : z.any(),
        questionnaires: buildQuestionnairesArraySchema(pendingQuestionnaires).default([]),
        // Shape matches PostSignupFormSchema; z.any() only relaxes hidden sections.
    }) as unknown as typeof PostSignupFormSchema;
}
